import { AnimatePresence, motion } from 'motion/react'
import { Control, ControllerProps, FieldPath, FieldValues } from 'react-hook-form'
import { useMeasure } from 'react-use'
import {
    FormControl,
    FormDescription,
    FormField as FormFieldReactHookForm,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/RHF/form'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'

type FormFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    // biome-ignore lint/suspicious/noExplicitAny: biome migration
    TContext = any,
> = {
    control: Control<TFieldValues, TContext>
    className?: string
    name: TName
    label?: string
    description?: string
    layout?: boolean | 'position'
    styleType: 'small' | 'medium'
} & ControllerProps<TFieldValues, TName>

export const FormField = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    // biome-ignore lint/suspicious/noExplicitAny: biome migration
    TContext = any,
>({
    control,
    className,
    name,
    label,
    render,
    description,
    layout = 'position',
    styleType,
    disabled,
}: FormFieldProps<TFieldValues, TName, TContext>) => {
    // --- STATE ---

    const [ref, { width }] = useMeasure<HTMLDivElement>()

    // --- RENDER ---

    return (
        <FormFieldReactHookForm
            control={control}
            name={name}
            disabled={disabled}
            render={(fieldProps) => (
                <motion.div
                    transition={{ layout: { duration: 0.1 } }}
                    layout={layout}
                    ref={ref}
                    className={cn('w-full', className)}
                >
                    <FormItem>
                        {label && (
                            <FormLabel
                                className={cn(
                                    'text-black ml-1 whitespace-nowrap',
                                    run(() => {
                                        switch (styleType) {
                                            case 'small':
                                                return 'text-[12px] font-medium ml-1 mb-1'

                                            case 'medium':
                                                return 'text-[14px] font-normal mb-1'
                                        }
                                    })
                                )}
                            >
                                {label}
                            </FormLabel>
                        )}

                        <FormControl>{render(fieldProps)}</FormControl>

                        {description && (
                            <FormDescription
                                className={cn(
                                    'dark:text-foreground/40 text-foreground/40 ml-1 -mt-1',
                                    run(() => {
                                        switch (styleType) {
                                            case 'small':
                                                return 'text-[12px] font-normal ml-1 mb-1'

                                            case 'medium':
                                                return 'text-[14px] font-normal mb-1'
                                        }
                                    })
                                )}
                            >
                                {description}
                            </FormDescription>
                        )}

                        <AnimatePresence initial={false}>
                            <FormMessage className="text-destructive" style={{ maxWidth: width }} />
                        </AnimatePresence>
                    </FormItem>
                </motion.div>
            )}
        />
    )
}
